import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import { navigate } from "../router";
import { whenLabel } from "../reminders";
import type { ScheduledMessage } from "../sync/types";
import { RichText } from "../text";
import { TimePicker } from "./TimePicker";
import { Badge, Card, EmptyState, Form, Page } from "./ui";
import { t } from "../i18n";

// ScheduledView is one's messages waiting to send: to send now, move to
// another time, edit or delete. One that could not be sent says why.
export function ScheduledView() {
  const session = useSession();
  const state = useLantern();
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [moving, setMoving] = useState<{ id: string; anchor: DOMRect } | null>(null);
  useEffect(() => void session.loadScheduled(), [session]);
  const list = state.scheduled;

  async function act(f: () => Promise<unknown>) {
    setError("");
    try {
      await f();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    await session.loadScheduled();
  }
  const where = (m: ScheduledMessage) => session.channelName(m.channel_id) + (m.thread_root_id ? " · " + t("in a thread") : "");

  return (
    <Page className="scheduled-view" title={t("Scheduled messages")} description={t("Messages you wrote to send later. Each is sent as you, at its time, if you are still in the channel.")}>
      {error && <p className="error">{error}</p>}
      {list === null && <p className="muted">{t("Loading…")}</p>}
      {list?.length === 0 && (
        <Card>
          <EmptyState icon="🕘" title={t("Nothing scheduled")}>
            {t("Write a message and pick the clock beside Send to send it later.")}
          </EmptyState>
        </Card>
      )}
      {list && list.length > 0 && (
        <Card>
          <ul className="rows scheduled-list">
            {list.map((m) => (
              <li key={m.id}>
                <div className="row-main">
                  <div className="inbox-kind">
                    {m.failed_at ? <Badge tone="bad">{t("Not sent")}</Badge> : <Badge tone="neutral">{t("Sends {when}", { when: whenLabel(new Date(m.send_at)) })}</Badge>}
                    <button className="link" onClick={() => navigate(m.thread_root_id ? `/c/${m.channel_id}/t/${m.thread_root_id}` : `/c/${m.channel_id}`)}>
                      {where(m)}
                    </button>
                  </div>
                  {m.failed_at && <p className="error">{m.error || t("It could not be sent.")}</p>}
                  {editing?.id === m.id ? (
                    <Form
                      className="scheduled-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        return act(() => session.api.updateScheduled(m.id, { text: session.mentionsIn(editing.text.trim()) })).then(() => setEditing(null));
                      }}
                    >
                      <textarea value={editing.text} onChange={(e) => setEditing({ id: m.id, text: e.target.value })} aria-label={t("Message")} rows={3} autoFocus />
                      <div className="form-actions">
                        <button type="button" onClick={() => setEditing(null)}>
                          {t("Cancel")}
                        </button>
                        <button type="submit" disabled={!editing.text.trim()}>
                          {t("Save")}
                        </button>
                      </div>
                    </Form>
                  ) : (
                    <div className="scheduled-text">
                      <RichText text={m.text} name={(id) => session.name(id)} me={state.me?.id} />
                    </div>
                  )}
                </div>
                {editing?.id !== m.id && (
                  <div className="scheduled-actions">
                    <button className="small" onClick={() => void act(() => session.api.updateScheduled(m.id, { send_at: new Date().toISOString() }))}>
                      {t("Send now")}
                    </button>
                    <button className="small" onClick={(e) => setMoving({ id: m.id, anchor: e.currentTarget.getBoundingClientRect() })}>
                      {m.failed_at ? t("Try again later") : t("Reschedule")}
                    </button>
                    <button className="small" onClick={() => setEditing({ id: m.id, text: plain(m.text, (id) => "@" + (session.directory.get(id)?.handle ?? id)) })}>
                      {t("Edit")}
                    </button>
                    <button className="small danger" onClick={() => void act(() => session.api.deleteScheduled(m.id))} aria-label={t("Delete scheduled message")}>
                      {t("Delete")}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {moving && (
        <TimePicker
          anchor={moving.anchor}
          label={t("Send at")}
          title={t("Reschedule message")}
          action={t("Reschedule")}
          onClose={() => setMoving(null)}
          onPick={(at) => void act(() => session.api.updateScheduled(moving.id, { send_at: at.toISOString() }))}
        />
      )}
    </Page>
  );
}

// plain turns mentions back into the @handles one types.
function plain(text: string, handle: (id: string) => string) {
  return text.replace(/<@([0-9a-f-]{36})>/g, (_, id: string) => handle(id)).replace(/<!subteam\^[0-9a-f-]{36}\|(@[^>]+)>/g, "$1");
}
